import Component, { Topic } from 'component';
import { assert, uuid, qtyInputOverflowHandle } from 'utils';
import { BUTTON_LOADING_ATTR } from 'constants';

export const FORM_ATTR = 'data-product-form';
export const ADD_BUTTON_ATTR = 'data-add-button';
export const VARIANT_ID_INPUT = 'id';
export const QTY_INPUT = 'quantity';

// Selectors for install addon feature
const INSTALL_CHECKBOX_SELECTOR = '#alfred-install-addon';
const INSTALL_VARIANT_INPUT_SELECTOR = '#alfred-install-variant-id';
const QTY_INPUT_SELECTOR = 'input[name="quantity"]';

customElements.define('x-product-form', class extends Component {
  constructor() {
    super();
  }

  form = null;
  TUID = null;
  addButton = null;
  submitHandler = null;

  render() {
    this.form = this.querySelector(`[${FORM_ATTR}]`);
    assert(this.form, 'Product form element is not defined');

    this.addButton = this.querySelector(`[${ADD_BUTTON_ATTR}]`);

    this.setupCartUpdateSubscription();
    this.setupFormSubmission();
  }

  setupCartUpdateSubscription() {
    this.sub(
      Topic.CART_UPDATE,
      ({ TUID, data, error }) => {
        if (this.TUID && this.TUID === TUID) {
          this.loading(false);
          this.TUID = null;
        }
      },
      { global: true }
    );
  }

  setupFormSubmission() {
    this.submitHandler = this.handleSubmit.bind(this);
    this.on(this.form, 'submit', this.submitHandler);
  }

  handleSubmit(e) {
    e.preventDefault();

    if (this.TUID) {
      console.warn('Previous add-to-cart transaction is still in progress');
      return;
    }

    const baseData = new FormData(this.form);
    const variantId = baseData.get(VARIANT_ID_INPUT);

    if (!variantId) {
      console.error('Variant ID is missing from form data');
      return;
    }

    this.cleanEmptyFields(baseData);

    const dataToSend = this.prepareFormData(baseData, variantId);

    this.submitToCart(dataToSend);
  }

  cleanEmptyFields(formData) {
    const keysToDelete = [];
    for (const [key, value] of formData.entries()) {
      if (typeof value === 'string' && value.trim() === '') {
        keysToDelete.push(key);
      }
    }
    keysToDelete.forEach(key => formData.delete(key));
  }

  prepareFormData(baseData, variantId) {
    const installConfig = this.getInstallAddonConfig();

    if (!installConfig.shouldAttach) {
      return baseData;
    }

    try {
      return this.buildMultiItemFormData(baseData, variantId, installConfig);
    } catch (err) {
      console.error('Failed to build multi-item form data:', err);
      return baseData;
    }
  }

  getInstallAddonConfig() {
    const installCheckbox = this.form.querySelector(INSTALL_CHECKBOX_SELECTOR);
    const installVariantInput = this.form.querySelector(INSTALL_VARIANT_INPUT_SELECTOR);
    const qtyInput = this.form.querySelector(QTY_INPUT_SELECTOR);

    const isSlatwallProduct = !!installVariantInput;
    const isChecked = installCheckbox?.checked ?? false;
    const hasRequiredElements = !!(installCheckbox && installVariantInput && qtyInput);

    return {
      shouldAttach: isSlatwallProduct && isChecked && hasRequiredElements,
      installVariantId: installVariantInput?.value,
      quantity: qtyInput ? parseInt(qtyInput.value, 10) || 1 : 1
    };
  }

  buildMultiItemFormData(baseData, slatVariantId, installConfig) {
    const multiData = new FormData();

    // Copy all properties except id and quantity (those go into items array)
    for (const [key, value] of baseData.entries()) {
      if (key !== 'id' && key !== 'quantity') {
        multiData.append(key, value);
      }
    }

    const qty = String(installConfig.quantity);

    // Add main slat product
    multiData.append('items[0][id]', String(slatVariantId));
    multiData.append('items[0][quantity]', qty);

    // Add install product
    multiData.append('items[1][id]', String(installConfig.installVariantId));
    multiData.append('items[1][quantity]', qty);

    return multiData;
  }

  submitToCart(formData) {
    this.loading(true);
    this.TUID = uuid();
    
    this.pub(Topic.CART_ADD, {
      TUID: this.TUID,
      formData
    });
  }

  loading(state) {
    if (this.addButton) {
      this.addButton.toggleAttribute(BUTTON_LOADING_ATTR, state);
    }
  }

  handleQtyInput(e) {
    qtyInputOverflowHandle(e.target);
  }

  disconnectedCallback() {
    super.disconnectedCallback?.();
    // Clean up if needed
    this.TUID = null;
  }
});